import React, { useState, useEffect } from 'react';
import { Sparkles, Image, Timer, Video, Clock, AlertTriangle, Loader } from 'lucide-react';
import { generateCode, generateVideoFromCode, generateVideo } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { translations, exampleTasks } from '../translations';
import './PromptInputTabs.css';
import { getVideoQuotaStatus, getCreditStatus } from '../services/api';

export default function PromptInputTabs({ onVideoGenerated, onBeforeGenerate }) {
  const { language: uiLanguage } = useLanguage();
  const t = translations[uiLanguage];
  const [activeTab] = useState('quick-prompt');
  
  // Service status
  /*const [serviceStatus, setServiceStatus] = useState({
    status: 'checking',
    message: t.prompt?.statusChecking || 'Checking service status...'
  });*/
  
  // Common states
  const [language, setLanguage] = useState(uiLanguage); // Sync with UI language
  const [quality, setQuality] = useState('qm');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(60);
  const [loading, setLoading] = useState(false);
  
  // AI Generate tab states
  const [prompt, setPrompt] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [aiSceneName, setAiSceneName] = useState('');
  const [aiStep, setAiStep] = useState(1);
  
  // Direct Code tab states
  const [directCode, setDirectCode] = useState('');
  const [directSceneName, setDirectSceneName] = useState('');
  const [autoExtractedScene, setAutoExtractedScene] = useState('');
  
  // Video quota status
  const [videoQuota, setVideoQuota] = useState({
    loading: true,
    hasReachedLimit: false,
    uploadsToday: 0,
    remainingToday: 0,
    resetsAtLocal: null,
    subscriptionPlan: 'free' // Track user plan
  });
  
  // Quick Prompt tab states
  const [quickPrompt, setQuickPrompt] = useState('');

  // NEW: Loading modal state
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Sync video language with UI language
  useEffect(() => {
    setLanguage(uiLanguage);
  }, [uiLanguage]);

  // Reset premium settings if user is free
  useEffect(() => {
    if (videoQuota.subscriptionPlan === 'free') {
      // Reset quality if higher than 720p
      if (quality === 'qh' || quality === 'qk') {
        setQuality('qm');
      }
      // Reset duration if longer than 2 minutes
      if (duration > 120) {
        setDuration(120);
      }
    }
  }, [videoQuota.subscriptionPlan, quality, duration]);



  useEffect(() => {
    const checkServiceStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/worker-status`);
        await response.json();
      } catch (error) {
        // Service status check failed silently — not critical
      }
    };
    
    const fetchVideoQuotaStatus = async () => {
      try {
        const [quotaData, creditData] = await Promise.all([
          getVideoQuotaStatus(),
          getCreditStatus()
        ]);
        setVideoQuota({
          loading: false,
          hasReachedLimit: quotaData.has_reached_limit,
          uploadsToday: quotaData.uploads_today,
          remainingToday: quotaData.remaining_today,
          resetsAtLocal: quotaData.resets_at_local,
          subscriptionPlan: creditData.subscription_plan || 'free'
        });
      } catch (err) {
        console.error('Quota fetch failed', err);
        setVideoQuota((q) => ({ ...q, loading: false }));
      }
    };

    checkServiceStatus();
    fetchVideoQuotaStatus();
    const interval = setInterval(() => {
      checkServiceStatus();
      fetchVideoQuotaStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [t.prompt?.statusOffline]);

  const canGenerateVideos = () => {
    return (
      !videoQuota.hasReachedLimit &&
      !videoQuota.loading
    );
  };

  const getEstimatedRenderTime = (qual, ratio) => {
    const baseTime = {
      'ql': 30,
      'qm': 60,
      'qh': 120,
      'qk': 300
    }[qual] || 60;
    
    const ratioMultiplier = {
      '16:9': 1,
      '9:16': 1.1,
      '1:1': 0.9,
      '4:5': 1
    }[ratio] || 1;
    
    return Math.round(baseTime * ratioMultiplier);
  };

  const extractSceneName = (code) => {
    const match = code.match(/class\s+(\w+)\s*\(/);
    return match ? match[1] : '';
  };

  useEffect(() => {
    if (directCode) {
      const extracted = extractSceneName(directCode);
      setAutoExtractedScene(extracted);
      if (extracted && (!directSceneName || directSceneName === autoExtractedScene)) {
        setDirectSceneName(extracted);
      }
    } else {
      setAutoExtractedScene('');
    }
  }, [directCode, directSceneName, autoExtractedScene]);

  // ✅ UPDATED: Quick Prompt with modal
  const handleQuickPrompt = async (e) => {
    e.preventDefault();
    if (!quickPrompt.trim()) return;

    const doGenerate = async () => {
      setLoading(true);
      setShowLoadingModal(true);
      setLoadingMessage(t.prompt?.generatingVideo || 'Generating video, please wait...');

      try {
        const response = await generateVideo(quickPrompt, quality, language, aspectRatio, duration);
        setQuickPrompt('');
        
        setTimeout(() => {
          setShowLoadingModal(false);
          if (onVideoGenerated) {
            onVideoGenerated(response.video_id);
          }
        }, 500);
        
      } catch (err) {
        setShowLoadingModal(false);
        console.error('Error in handleQuickPrompt:', err.message);
      } finally {
        setLoading(false);
      }
    };

    if (onBeforeGenerate) {
      onBeforeGenerate(doGenerate);
    } else {
      doGenerate();
    }
  };

  // ✅ UPDATED: AI Generate Step 1 with modal
  const handleGenerateCode = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    const doGenerate = async () => {
      setLoading(true);
      setShowLoadingModal(true);
      setLoadingMessage(t.prompt?.generatingCode || 'Generating Manim code...');

      try {
        const response = await generateCode(prompt, language, duration);
        setGeneratedCode(response.code);
        setAiSceneName(response.scene_name);
        setAiStep(2);
        setShowLoadingModal(false);
      } catch (err) {
        setShowLoadingModal(false);
        console.error('Error in handleGenerateCode:', err.message);
      } finally {
        setLoading(false);
      }
    };

    if (onBeforeGenerate) {
      onBeforeGenerate(doGenerate);
    } else {
      doGenerate();
    }
  };

  // ✅ UPDATED: AI Generate Step 2 with modal
  const handleRenderFromAI = async () => {
    if (!generatedCode || !aiSceneName) return;

    const doGenerate = async () => {
      setLoading(true);
      setShowLoadingModal(true);
      setLoadingMessage(t.prompt?.renderingVideo || 'Rendering video, please wait...');

      try {
        const response = await generateVideoFromCode(
          generatedCode,
          aiSceneName,
          quality,
          language,
          aspectRatio
        );
        
        setPrompt('');
        setGeneratedCode('');
        setAiSceneName('');
        setAiStep(1);
        
        setTimeout(() => {
          setShowLoadingModal(false);
          if (onVideoGenerated) {
            onVideoGenerated(response.video_id);
          }
        }, 500);
        
      } catch (err) {
        setShowLoadingModal(false);
        console.error('Error in handleRenderFromAI:', err.message);
      } finally {
        setLoading(false);
      }
    };

    if (onBeforeGenerate) {
      onBeforeGenerate(doGenerate);
    } else {
      doGenerate();
    }
  };

  // ✅ UPDATED: Direct Code with modal
  const handleRenderFromCode = async (e) => {
    e.preventDefault();
    if (!directCode.trim() || !directSceneName.trim()) return;

    const doGenerate = async () => {
      setLoading(true);
      setShowLoadingModal(true);
      setLoadingMessage(t.prompt?.renderingVideo || 'Rendering video, please wait...');

      try {
        const response = await generateVideoFromCode(
          directCode,
          directSceneName,
          quality,
          language,
          aspectRatio
        );
        
        setDirectCode('');
        setDirectSceneName('');
        setAutoExtractedScene('');
        
        setTimeout(() => {
          setShowLoadingModal(false);
          if (onVideoGenerated) {
            onVideoGenerated(response.video_id);
          }
        }, 500);
        
      } catch (err) {
        setShowLoadingModal(false);
        console.error('Error in handleRenderFromCode:', err.message);
      } finally {
        setLoading(false);
      }
    };

    if (onBeforeGenerate) {
      onBeforeGenerate(doGenerate);
    } else {
      doGenerate();
    }
  };

  const handleExampleClick = (example) => {
    if (activeTab === 'quick-prompt') {
      setQuickPrompt(example);
    } else if (activeTab === 'ai-generate') {
      setPrompt(example);
    }
  };

  // Example Tasks Section Component
  const ExampleTasksSection = () => {
    const tasks = exampleTasks[uiLanguage] || exampleTasks.en;
    
    return (
      <div className="example-tasks-section">
        <div className="example-tasks-header">
          <h3 className="example-tasks-title">{tasks.sectionTitle}</h3>
        </div>
        
        <div className="example-tasks-grid">
          {/* Chemistry Task */}
          <div className="example-task-card">
            <div className="example-task-header">
              <h4 className="example-task-subject">{tasks.chemistry.title}</h4>
            </div>
            <div className="example-task-content">
              <p className="example-task-text">{tasks.chemistry.task}</p>
            </div>
            <button 
              className="btn-try-example"
              onClick={() => setQuickPrompt(tasks.chemistry.task)}
              disabled={loading || !canGenerateVideos()}
            >
              {t.prompt?.tryThisExample || 'Try this example'}
            </button>
          </div>
          
          {/* Physics Task */}
          <div className="example-task-card">
            <div className="example-task-header">
              <h4 className="example-task-subject">{tasks.physics.title}</h4>
            </div>
            <div className="example-task-content">
              <p className="example-task-text">{tasks.physics.task}</p>
            </div>
            <button 
              className="btn-try-example"
              onClick={() => setQuickPrompt(tasks.physics.task)}
              disabled={loading || !canGenerateVideos()}
            >
              {t.prompt?.tryThisExample || 'Try this example'}
            </button>
          </div>
          
          {/* Mathematics Task */}
          <div className="example-task-card">
            <div className="example-task-header">
              <h4 className="example-task-subject">{tasks.mathematics.title}</h4>
            </div>
            <div className="example-task-content">
              <p className="example-task-text">{tasks.mathematics.task}</p>
            </div>
            <button 
              className="btn-try-example"
              onClick={() => setQuickPrompt(tasks.mathematics.task)}
              disabled={loading || !canGenerateVideos()}
            >
              {t.prompt?.tryThisExample || 'Try this example'}
            </button>
          </div>
        </div>
        
        {/* Disclaimer */}
        <div className="example-tasks-disclaimer">
          <div className="disclaimer-icon">
            <AlertTriangle size={20} />
          </div>
          <p className="disclaimer-text">{tasks.disclaimer}</p>
        </div>
      </div>
    );
  };

  const SettingsPanel = () => {
  const isFreeUser = videoQuota.subscriptionPlan === 'free';

    return (
    <div className="settings-panel">
      <div className="settings-grid-2x2">
        {/* Duration */}
        <div className="setting-group">
          <label className="settings-label">
            <Timer size={18} />
            <span>{t.prompt?.duration || 'Duration'}</span>
          </label>
          <select
            className="settings-select"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            disabled={loading}
          >
            <option value={30}>⚡ 30s - {t.prompt?.quick || 'Quick'}</option>
            <option value={60}>📺 1min - {t.prompt?.standard || 'Standard'}</option>
            <option value={120}>📖 2min - {t.prompt?.detailed || 'Detailed'}</option>
            <option value={180} disabled={isFreeUser}>📚 3min - {t.prompt?.comprehensive || 'Comprehensive'} {isFreeUser ? '🔒 Pro' : ''}</option>
            <option value={300} disabled={isFreeUser}>🎓 5min - {t.prompt?.indepth || 'In-depth'} {isFreeUser ? '🔒 Pro' : ''}</option>
          </select>
        </div>

        {/* Quality */}
        <div className="setting-group">
          <label className="settings-label">
            <Sparkles size={18} />
            <span>{t.prompt?.quality || 'Quality'}</span>
          </label>
          <select
            className="settings-select"
            value={quality}
            onChange={(e) => setQuality(e.target.value)}
            disabled={loading}
          >
            <option value="ql">📱 480p - {t.prompt?.fast || 'Fast'} (~30s)</option>
            <option value="qm">💻 720p - {t.prompt?.recommended || 'Recommended'} (~1min)</option>
            <option value="qh" disabled={isFreeUser}>🖥️ 1080p HD - {t.prompt?.high || 'High'} (~2min) {isFreeUser ? '🔒 Pro' : ''}</option>
            <option value="qk" disabled={isFreeUser}>🎬 4K - {t.prompt?.best || 'Best'} (~5min) {isFreeUser ? '🔒 Pro' : ''}</option>
          </select>
        </div>

        {/* Aspect Ratio */}
        <div className="setting-group">
          <label className="settings-label">
            <Image size={18} />
            <span>{t.prompt?.aspectRatio || 'Aspect Ratio'}</span>
          </label>
          <select
            className="settings-select"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={loading}
          >
            <option value="16:9">📺 16:9 - {t.prompt?.landscape || 'Landscape'}</option>
            <option value="9:16">📱 9:16 - {t.prompt?.vertical || 'Vertical'}</option>
            <option value="1:1">⬜ 1:1 - {t.prompt?.square || 'Square'}</option>
            <option value="4:5">📐 4:5 - {t.prompt?.portrait || 'Portrait'}</option>
          </select>
        </div>

        {/* Language */}
        <div className="setting-group">
          <label className="settings-label">
            <Video size={18} />
            <span>{t.prompt?.voiceoverLanguage || 'Voiceover'}</span>
          </label>
          <select
            className="settings-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={loading}
          >
            <option value="fi">🇫🇮 {t.prompt?.finnish || 'Finnish'}</option>
            <option value="en">🇬🇧 {t.prompt?.english || 'English'}</option>
            <option value="sv">🇸🇪 {t.prompt?.swedish || 'Swedish'}</option>
          </select>
        </div>
      </div>

      <div className="render-time-estimate">
        <Clock size={16} />
        <span>{t.prompt?.estimatedRenderTime || 'Estimated render time'}: ~{getEstimatedRenderTime(quality, aspectRatio)}s</span>
      </div>
    </div>
    );
  };

  return (
    <div className="prompt-input-tabs">
      {/* Daily Limit Warning */}
      {videoQuota.hasReachedLimit && (
        <div className="quota-warning">
          <div className="quota-icon">⚠️</div>
          <div className="quota-message">
            <strong>{t.prompt?.dailyLimitReached || 'Daily video limit reached'}</strong>
            <p>
              {t.prompt?.dailyLimitMessage?.replace('{count}', videoQuota.uploadsToday) || 
                `You've created ${videoQuota.uploadsToday} videos today.`}
              {' '}
              {t.prompt?.resetsAt || 'Resets at'}: {videoQuota.resetsAtLocal}
            </p>
          </div>
        </div>
      )}

      {/* Service Status Bar */}
	  {/*
      {serviceStatus.status !== 'online' && (
        <div className={`service-status status-${serviceStatus.status}`}>
          {serviceStatus.status === 'checking' && (
            <span>🔄 {serviceStatus.message}</span>
          )}
          {serviceStatus.status === 'busy' && (
            <span>⏳ {serviceStatus.message}</span>
          )}
          {serviceStatus.status === 'offline' && (
            <span>❌ {serviceStatus.message}</span>
          )}
        </div>
      )}
	  */}

      {/* Tab Content */}
      <div className="tab-content">

        {/* Quick Prompt Tab */}
        {activeTab === 'quick-prompt' && (
          <>
            <form className="tab-form" onSubmit={handleQuickPrompt}>
              <div className="tab-description">
                <h2 className="tab-description-title">
                  {t.prompt?.quickGenerateTitle || 'Quick Generate'}
                </h2>
                <p className="tab-description-text">
                  {t.prompt?.quickGenerateDesc || 'Instant AI video generation - describe what you want to animate and get your video in seconds'}
                </p>
              </div>

              <div className="form-group">
                <label>
                  <span>{t.prompt?.whatToAnimate || 'What do you want to animate?'}</span>
                </label>
                <textarea
                  value={quickPrompt}
                  onChange={(e) => setQuickPrompt(e.target.value)}
                  placeholder={t.prompt?.quickPlaceholder || 'Example: Explain ionic bonding between sodium and chlorine...'}
                  disabled={loading || !canGenerateVideos()}
                  rows={4}
                />
              </div>

              <SettingsPanel />

              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !quickPrompt.trim() || !canGenerateVideos()}
              >
                {loading ? (t.prompt?.generatingVideo || '🎬 Generating Video...') : (t.prompt?.generateVideo || '🚀 Generate Video')}
              </button>
              
            </form>

            {/* Example Tasks Section - NEW */}
            <ExampleTasksSection />
          </>
        )}

        {/* AI Generate Tab */}
        {activeTab === 'ai-generate' && (
          <div className="tab-form">
            <div className="tab-description">
              <h2 className="tab-description-title">
                {t.prompt?.aiGenerateTitle || 'AI Generate'}
              </h2>
              <p className="tab-description-text">
                {t.prompt?.aiGenerateDesc || 'Review and edit the AI-generated code before rendering your video'}
              </p>
            </div>

            {aiStep === 1 ? (
              <form onSubmit={handleGenerateCode}>
                <div className="form-group">
                  <label>
                    <span>{t.prompt?.describeAnimation || 'Describe your animation'}</span>
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={t.prompt?.aiPlaceholder || 'Example: Create an animation showing ionic bonding...'}
                    disabled={loading || !canGenerateVideos()}
                    rows={4}
                  />
                </div>

                <SettingsPanel />

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !prompt.trim() || !canGenerateVideos()}
                >
                  {loading ? (t.prompt?.generatingCode || '⚙️ Generating Code...') : (t.prompt?.generateCode || '✨ Generate Code')}
                </button>

                <div className="examples">
                  <p>💡 {t.prompt?.tryExamples || 'Try these examples'}:</p>
                  <ul>
                    <li onClick={() => handleExampleClick(t.prompt?.exampleCovalent || 'Explain covalent bonding')}>
                      {t.prompt?.exampleCovalent || 'Explain covalent bonding'}
                    </li>
                    <li onClick={() => handleExampleClick(t.prompt?.exampleElectron || 'Show electron configuration of oxygen')}>
                      {t.prompt?.exampleElectron || 'Show electron configuration of oxygen'}
                    </li>
                  </ul>
                </div>
              </form>
            ) : (
              <div className="code-review-section">
                <div className="form-group">
                  <label>
                    <span>{t.prompt?.generatedCode || 'Generated Code'}</span>
                    <button
                      type="button"
                      className="edit-code-btn"
                      onClick={() => {
                        setGeneratedCode('');
                        setAiStep(1);
                      }}
                    >
                      ← {t.prompt?.backToEdit || 'Back to Edit'}
                    </button>
                  </label>
                  <textarea
                    className="code-textarea"
                    value={generatedCode}
                    onChange={(e) => setGeneratedCode(e.target.value)}
                    rows={15}
                    disabled={loading}
                  />
                  <span className="code-hint">
                    {t.prompt?.scene || 'Scene'}: {aiSceneName}
                  </span>
                </div>

                <SettingsPanel />

                <div className="button-group">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setAiStep(1)}
                    disabled={loading}
                  >
                    ← {t.back || 'Back'}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleRenderFromAI}
                    disabled={loading || !canGenerateVideos()}
                  >
                    {loading ? (t.prompt?.rendering || '🎬 Rendering...') : (t.prompt?.renderVideo || '🎬 Render Video')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Direct Code Tab */}
        {activeTab === 'direct-code' && (
          <form className="tab-form" onSubmit={handleRenderFromCode}>
            <div className="tab-description">
              <h2 className="tab-description-title">
                {t.prompt?.customCodeTitle || 'Custom Code'}
              </h2>
              <p className="tab-description-text">
                {t.prompt?.customCodeDesc || 'Write your own Manim code with full control over the animation'}
              </p>
            </div>

            <div className="form-group">
              <label>
                <span>{t.prompt?.sceneName || 'Scene Name'}</span>
                {autoExtractedScene && (
                  <span className="auto-detected">✓ {t.prompt?.autoDetected || 'Auto-detected'}: {autoExtractedScene}</span>
                )}
              </label>
              <input
                type="text"
                className="scene-name-input"
                value={directSceneName}
                onChange={(e) => setDirectSceneName(e.target.value)}
                placeholder="MyScene"
                disabled={loading || !canGenerateVideos()}
              />
            </div>

            <div className="form-group">
              <label>
                <span>{t.prompt?.manimCode || 'Manim Code'}</span>
              </label>
              <textarea
                className="code-textarea"
                value={directCode}
                onChange={(e) => setDirectCode(e.target.value)}
                placeholder={t.prompt?.codePlaceholder || 'from manim import *\nfrom manim_voiceover import VoiceoverScene\n...'}
                rows={15}
                disabled={loading || !canGenerateVideos()}
              />
              <span className="code-hint">
                {t.prompt?.codeRequirement || 'Must include VoiceoverScene and all required imports'}
              </span>
            </div>

            <SettingsPanel />

            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !directCode.trim() || !directSceneName.trim() || !canGenerateVideos()}
            >
              {loading ? (t.prompt?.rendering || '🎬 Rendering...') : (t.prompt?.renderVideo || '🎬 Render Video')}
            </button>
          </form>
        )}
      </div>

      {/* ✅ NEW: Loading Modal */}
      {showLoadingModal && (
        <div className="loading-modal-overlay">
          <div className="loading-modal">
            <div className="loading-spinner">
              <Loader size={48} className="spinner-icon" />
            </div>
            <h3 className="loading-title">
              {loadingMessage}
            </h3>
            <p className="loading-subtitle">
              {t.prompt?.pleaseWait || 'This may take a few moments...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}